import { LocalizationModule } from '@abp/ng.core';
import { ChangeDetectionStrategy, Component, inject, input } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DwAlertComponent } from '@digiwall/core';
import { DialogBoxComponent, TableComponent, TableRowComponent } from '@digiwall/ui-kit';
import { PlanningDataSource } from '@digiwall/goliath-documents/proxy';
import { PlanningCardFieldDescriptor } from '../adapters/planning-card-field';
import { PlanningCardFieldVisibilityStore } from './planning-card-field-visibility.store';

/** One data source's configurable card fields, as shown in the config panel. */
export interface PlanningCardConfigSource {
  dataSource: PlanningDataSource;
  label: string;
  cardFields: PlanningCardFieldDescriptor[];
}

/**
 * Right-panel content listing each data source's toggleable card fields, with an independent
 * show/hide switch for the backlog and for the planned column (backlog first, mirroring the board
 * layout). Toggles apply live (the visibility store persists each change) — there is no save button.
 * Rendered inside the board's offcanvas.
 */
@Component({
  selector: 'document-planning-card-config-panel',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    LocalizationModule,
    DwAlertComponent,
    DialogBoxComponent,
    TableComponent,
    TableRowComponent,
  ],
  template: `
    <dw-ui-dialog-box [title]="'Doc::Planning:CardFields'" [displayFooter]="false">
      <dw-core-ui-alert
        type="info"
        icon="digi-out-alert-info"
        text="Doc::Planning:CardFields:Info"
      ></dw-core-ui-alert>
      @for (source of sources(); track source.dataSource) {
        @if (sources().length > 1) {
          <h6 class="mt-2 mb-1">{{ source.label }}</h6>
        }
        <dw-ui-table>
          <ng-container header>
            <th></th>
            <th class="text-center">
              {{ 'Doc::Planning:CardFields:Backlog' | abpLocalization }}
            </th>
            <th class="text-center">
              {{ 'Doc::Planning:CardFields:Planned' | abpLocalization }}
            </th>
          </ng-container>

          @for (field of source.cardFields; track field.key) {
            <dw-ui-table-row>
              <td [class.text-muted]="field.fixed">{{ field.labelKey | abpLocalization }}</td>
              <td class="text-center">
                <div class="form-check form-switch d-inline-block">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    [disabled]="field.fixed"
                    [ngModel]="field.fixed || store.isVisible(source.dataSource, 'backlog', field.key)"
                    [ngModelOptions]="{ standalone: true }"
                    (ngModelChange)="store.setVisible(source.dataSource, 'backlog', field.key, $event)"
                  />
                </div>
              </td>
              <td class="text-center">
                <div class="form-check form-switch d-inline-block">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    [disabled]="field.fixed"
                    [ngModel]="field.fixed || store.isVisible(source.dataSource, 'planned', field.key)"
                    [ngModelOptions]="{ standalone: true }"
                    (ngModelChange)="store.setVisible(source.dataSource, 'planned', field.key, $event)"
                  />
                </div>
              </td>
            </dw-ui-table-row>
          }
        </dw-ui-table>
      }
    </dw-ui-dialog-box>
  `,
})
export class PlanningCardConfigPanelComponent {
  protected readonly store = inject(PlanningCardFieldVisibilityStore);

  protected readonly sources = input.required<PlanningCardConfigSource[]>();
}
